import React, { memo, useMemo } from 'react'
import { useSelector } from 'react-redux'
import { EmptyTableMessage } from 'src/components/payments-ops/EmptyTableMessage'
import { GridTable } from 'src/components/shared/grid/GridTable'
import { buildBankOnboardingColumns } from 'src/constants/payments-ops/bankOnboardingsGrid'
import { PAYMENTS_OPS_PAGE_SIZE_OPTIONS } from 'src/constants/payments-ops/constants'
import type { BankOnboarding } from 'src/interfaces/bankOnboarding'
import { getBankOnboardings, getBankOnboardingsLoading } from 'src/selectors/bankOnboarding'
import { useGridPagination } from 'src/utils/hooks/grid/useGridPagination'

interface Props {
  /** null without `bank_onboarding_manage` — the actions column is dropped. */
  onStartNewFrom: ((onboarding: BankOnboarding) => void) | null
}

const getRowId = (row: BankOnboarding): string => {
  return row.id
}

const renderEmpty = () => {
  return <EmptyTableMessage message="No bank onboardings match these filters." />
}

const BankOnboardingsTableComponent = ({ onStartNewFrom }: Props) => {
  const onboardings = useSelector(getBankOnboardings)
  const isLoading = useSelector(getBankOnboardingsLoading)
  const { paginationModel, setPaginationModel } = useGridPagination(PAYMENTS_OPS_PAGE_SIZE_OPTIONS)

  const rows = useMemo(() => {
    return [...onboardings].sort((left, right) => {
      return (right.createdAt ?? 0) - (left.createdAt ?? 0)
    })
  }, [onboardings])

  const columns = useMemo(() => {
    return buildBankOnboardingColumns(onStartNewFrom)
  }, [onStartNewFrom])

  return (
    <GridTable<BankOnboarding>
      rows={rows}
      columns={columns}
      getRowId={getRowId}
      isLoading={isLoading}
      fillHeight
      sortingMode="client"
      paginationMode="client"
      paginationModel={paginationModel}
      onPaginationModelChange={setPaginationModel}
      pageSizeOptions={PAYMENTS_OPS_PAGE_SIZE_OPTIONS}
      EmptyElement={renderEmpty}
      tableDataTestId="bank-onboarding-table"
      rowDataTestIdPrefix="bank-onboarding"
    />
  )
}

export const BankOnboardingsTable = memo(BankOnboardingsTableComponent)
